// Type definitions matching the Supabase schema in
// vonlof_backend/supabase/migrations/0002_tables.sql.
// These are hand-written to avoid the typescript-types generation step.

export interface SiteSettings {
  brand: {
    name: string;
    legal_name: string;
    tagline: string;
    logo_url: string | null;
    wordmark_url: string | null;
    favicon_url: string | null;
  };
  contact: {
    email: string;
    phone: string;
    address_line: string;
    business_hours: string;
  };
  social: {
    linkedin: string | null;
    github: string | null;
    x: string | null;
    youtube: string | null;
    instagram: string | null;
    tiktok: string | null;
    facebook: string | null;
  };
  emails: {
    press: string;
    careers: string;
    accessibility: string;
    security: string;
  };
  theme: {
    default: 'dark' | 'light' | 'system';
    gold_primary: string;
    bg: string;
  };
  nav: {
    primary: Array<{ label: string; href: string }>;
    cta_label: string;
    cta_href: string;
  };
  footer: {
    copyright: string;
  };
  seo: {
    default_title_suffix: string;
    default_description: string;
    default_og_image_url: string | null;
    twitter_handle: string | null;
  };
  analytics: {
    cloudflare_web_analytics_enabled: boolean;
    sentry_dsn: string | null;
    plausible_domain: string | null;
  };
  email_sender: {
    from_name: string;
    from_email: string;
    reply_to: string;
  };
}

export type HomeSectionKey =
  | 'hero'
  | 'trusted_by'
  | 'mission'
  | 'services_preview'
  | 'featured_projects'
  | 'tech_stack'
  | 'industries'
  | 'process'
  | 'testimonials'
  | 'cta_band'
  | 'latest_posts';

export interface HomeHero {
  eyebrow?: string;
  headline: string;
  sub: string;
  primary_cta_label: string;
  primary_cta_href: string;
  secondary_cta_label?: string;
  secondary_cta_href?: string;
  show_scroll_cue?: boolean;
  background_variant?: 'gradient' | 'plain' | 'image';
}

export interface HomeTrustedBy {
  headline: string;
  logos: Array<{ media_id?: string; alt: string; href?: string }>;
}

export interface HomeMission {
  eyebrow?: string;
  headline: string;
  body: string;
  pull_quote?: string;
  pull_quote_attr?: string;
}

export interface HomeServicesPreview {
  headline: string;
  view_all_label: string;
  view_all_href: string;
  featured_service_ids?: string[];
}

export interface HomeFeaturedProjects {
  headline: string;
  count: number;
  manual_project_ids?: string[];
  view_all_label?: string;
  view_all_href?: string;
}

export interface HomeTechStack {
  items: string[];
}

export interface HomeIndustries {
  items: Array<{ icon: string; name: string; outcome: string }>;
}

export interface HomeProcess {
  items: Array<{
    step: number;
    title: string;
    description: string;
    deliverables: string[];
  }>;
}

export interface HomeTestimonials {
  items: Array<{
    quote: string;
    author: string;
    role: string;
    company: string;
    avatar_media_id?: string;
    logo_media_id?: string;
  }>;
}

export interface HomeCtaBand {
  headline: string;
  primary_cta_label: string;
  primary_cta_href: string;
  variant?: 'gold' | 'dark';
}

export interface HomeLatestPosts {
  headline: string;
  count: number;
  view_all_label: string;
  view_all_href: string;
}

export interface HomeServicesPreview {
  headline: string;
  view_all_label: string;
  view_all_href: string;
  featured_service_ids?: string[];
}

export type HomeSectionData =
  | { kind: 'hero'; data: HomeHero }
  | { kind: 'trusted_by'; data: HomeTrustedBy }
  | { kind: 'mission'; data: HomeMission }
  | { kind: 'services_preview'; data: HomeServicesPreview }
  | { kind: 'featured_projects'; data: HomeFeaturedProjects }
  | { kind: 'tech_stack'; data: HomeTechStack }
  | { kind: 'industries'; data: HomeIndustries }
  | { kind: 'process'; data: HomeProcess }
  | { kind: 'testimonials'; data: HomeTestimonials }
  | { kind: 'cta_band'; data: HomeCtaBand }
  | { kind: 'latest_posts'; data: HomeLatestPosts };

export interface HomeSection {
  id: string;
  key: HomeSectionKey;
  display_order: number;
  is_enabled: boolean;
  data: Record<string, unknown>;
}

export type AboutKey =
  | 'story'
  | 'mission'
  | 'vision'
  | 'why_we_exist'
  | 'core_values'
  | 'timeline'
  | 'achievements'
  | 'roadmap';

export interface AboutContent {
  id: string;
  key: AboutKey;
  data: Record<string, unknown>;
}

export type Department = 'leadership' | 'engineering' | 'design' | 'product' | 'operations';
export const DEPARTMENT_VALUES: Department[] = ['leadership', 'engineering', 'design', 'product', 'operations'];

export interface TeamMember {
  id: string;
  slug: string;
  name: string;
  role: string;
  departments: Department[];
  bio: string | null;
  bio_md: string | null;
  skills: string[];
  portrait_url: string | null;
  linkedin_url: string | null;
  github_url: string | null;
  email: string | null;
  display_order: number;
  is_active: boolean;
}

export type ProjectStatus = 'shipped' | 'ongoing' | 'concept';
export type ProjectCategory =
  | 'AI'
  | 'Web'
  | 'Mobile'
  | 'SaaS'
  | 'Government'
  | 'Healthcare'
  | 'Education'
  | 'Finance'
  | 'E-commerce';

export interface Project {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  description_md: string | null;
  problem: string | null;
  approach: string | null;
  client: string | null;
  year: number | null;
  status: ProjectStatus;
  category: ProjectCategory;
  tech_stack: string[];
  hero_image_url: string | null;
  gallery: ProjectImage[];
  features: ProjectFeature[];
  business_impact: Array<{ value: string; label: string; sublabel?: string }>;
  cta_label: string | null;
  cta_href: string | null;
  is_featured: boolean;
  display_order: number;
  published_at: string | null;
}

export interface ProjectFeature {
  id: string;
  icon: string | null;
  title: string;
  description: string | null;
  display_order: number;
}

export interface ProjectImage {
  id: string;
  url: string;
  alt: string;
  display_order: number;
}

export type PostStatus = 'draft' | 'scheduled' | 'published' | 'archived';

export interface Post {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_markdown: string;
  hero_image_url: string | null;
  author_id: string | null;
  author: Pick<TeamMember, 'id' | 'name' | 'role' | 'slug' | 'portrait_url'> | null;
  category: string;
  tags: string[];
  reading_time_min: number | null;
  status: PostStatus;
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Tiered-pricing payload for services like Short AI Video Content.
 * Optional — services without `pricing` render the standard
 * "deliverables + tech strip" layout.
 */
export interface ServicePricingTier {
  name: string;
  bestFor: string;
  length: string;
  includes: string[];
  priceGHS: number;
  priceUSD: number;
  popular: boolean;
}

export interface ServicePricingPackage {
  name: string;
  videosPerMonth: string;
  bestFor: string;
  priceGHS: number;
  priceUSD: number;
  popular: boolean;
}

export interface ServicePricingAddon {
  name: string;
  price: string;
}

export interface ServicePricing {
  headline?: string;
  lede?: string;
  categories?: string[];
  tiers: ServicePricingTier[];
  monthly: ServicePricingPackage[];
  addons: ServicePricingAddon[];
  rules: {
    footage?: string;
    revisions?: string;
    payment?: string;
    delivery?: string;
  };
  bundle?: {
    headline: string;
    body: string;
    ctaLabel?: string;
    ctaHref?: string;
  };
}

export interface Service {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  description_md: string | null;
  icon: string | null;
  deliverables: string[];
  tech_strip: string[];
  cta_label: string;
  cta_href: string;
  display_order: number;
  is_featured: boolean;
  meta_title: string | null;
  meta_description: string | null;
  topic_slug: string | null;
  pricing: ServicePricing | null;
}

export interface Topic {
  id: string;
  name: string;
  slug: string;
  display_order: number;
  is_active: boolean;
}

export interface Industry {
  id: string;
  slug: string;
  name: string;
  icon: string | null;
  outcome: string | null;
  display_order: number;
}

export type InfluencerStatus = 'draft' | 'published' | 'archived';

export interface Influencer {
  id: string;
  slug: string;
  name: string;
  headline: string | null;
  bio_md: string | null;
  portrait_url: string | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  /** Facebook page URL (renamed from the X/Twitter slot in migration 0017). */
  facebook_url: string | null;
  joined_at: string | null;
  is_active: boolean;
}

export type ContentCategory = 'short-drama' | 'micro-series' | 'promo' | 'behind-the-scenes';
export type ContentStatus = 'draft' | 'published' | 'archived';

export interface Content {
  id: string;
  slug: string;
  title: string;
  summary: string;
  description_md: string | null;
  cover_url: string | null;
  primary_influencer: Influencer | null;
  tiktok_url: string | null;
  instagram_url: string | null;
  youtube_url: string | null;
  x_url: string | null;
  category: ContentCategory;
  is_featured: boolean;
  published_at: string | null;
}

export type RoleType = 'full-time' | 'part-time' | 'contract' | 'intern';

export interface Role {
  id: string;
  slug: string;
  title: string;
  department: string | null;
  location: string | null;
  type: RoleType | null;
  description_md: string | null;
  responsibilities: string[];
  requirements: string[];
  nice_to_haves: string[];
  is_open: boolean;
  apply_email: string | null;
  published_at: string | null;
}

export interface FaqItem {
  id: string;
  question: string;
  answer_md: string;
  category: string;
  display_order: number;
  is_published: boolean;
}

export interface LegalPage {
  id: string;
  slug: 'privacy' | 'terms' | 'cookies';
  title: string;
  body_md: string;
  effective_at: string | null;
  updated_at: string;
}

export interface FooterLink {
  id: string;
  column: 'company' | 'projects' | 'services' | 'insights';
  display_order: number;
  label: string;
  href: string;
  is_external: boolean;
}

export interface MediaAsset {
  id: string;
  bucket: 'public' | 'cvs';
  path: string;
  url: string;
  filename: string;
  alt: string;
  width: number | null;
  height: number | null;
  bytes: number | null;
  mime: string;
  tags: string[];
}